import React from "react";
import { createPortal } from "react-dom";
import BottomSheet from "./BottomSheet";
import type { PayoutBatch } from "../types/payouts";
import { formatMoneyByCurrency, formatSingleAmount, moneyByCurrencyFrom } from "../utils/currency";
import { payoutDestinationView } from "../utils/payoutDestination";
import {
  aboutDuration,
  hasLiveLeg,
  trackerDate,
  trackerKind,
  trackerLegs,
  trackerShortDate,
  trackerStops,
  type TrackerKind,
} from "../utils/payoutTracker";
import { t } from "../i18n";
import { T } from "../i18n/T";
import "./PayoutTracker.css";

// The payout tracker (1.5.2, founder pick 2026-10-22; renders in
// .claude/1.5.2-payout-tracker-r2.html): the green "on its way" strip under
// Balance's Recent Payouts line, the same bar on a Payout History row, and the
// sheet either one opens. The "First payouts can take a bit longer" box is the
// founder's own words; its two numbers come from the server (speed), which
// sends them only while faster payouts are certain.

export const payoutAmount = (p: PayoutBatch): string =>
  p.totalsByCurrency && Object.keys(p.totalsByCurrency).length > 0
    ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "net"))
    : formatSingleAmount(p.totalNet, "usd");

// Drawn, never an emoji: a bank, a debit card or a wallet.
export const TrackerIcon: React.FC<{ kind: TrackerKind; size?: number }> = ({ kind, size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {kind === "card" ? (
      <><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19" /></>
    ) : kind === "wallet" ? (
      <><path d="M19 7V5.5A1.5 1.5 0 0 0 17.5 4h-12A2.5 2.5 0 0 0 3 6.5v11A2.5 2.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" /><path d="M21 9h-5a3 3 0 0 0 0 6h5Z" /></>
    ) : (
      <><path d="M3 10 12 4l9 6" /><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" /><path d="M3 20h18" /></>
    )}
  </svg>
);

/** Dots for the stops, a leg between each, the leg in progress filled by time. */
export const TrackerBar: React.FC<{ payout: PayoutBatch; now?: Date }> = ({ payout, now }) => {
  const stops = trackerStops(payout);
  const legs = trackerLegs(payout, now);
  const live = hasLiveLeg(legs);
  return (
    <div className="ptk-bar" aria-hidden="true">
      {stops.map((s, i) => (
        <React.Fragment key={s.label}>
          <span className={`ptk-dot ptk-dot--${s.state}`} />
          {i < legs.length && (
            <span className={`ptk-leg${live && legs[i] > 0 && legs[i] < 1 ? " ptk-leg--live" : ""}`}>
              <span className="ptk-fill" style={{ width: `${Math.round(legs[i] * 100)}%` }} />
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/** The green strip: "$4.50 on its way · Arrives by Mon, Nov 2" and the bar. A button: it opens the sheet. */
export const PayoutOnItsWay: React.FC<{ payout: PayoutBatch; onOpen: () => void }> = ({ payout, onOpen }) => (
  <button type="button" className="ptk-strip" onClick={onOpen} aria-label={t("payoutTracker.openTracker")}>
    <span className="ptk-ico">
      <TrackerIcon kind={trackerKind(payout)} />
    </span>
    <span className="ptk-strip-main">
      <span className="ptk-strip-title">
        <span className="ptk-strip-amount">{t("payoutTracker.onItsWay", { amount: payoutAmount(payout) })}</span>
        {payout.speed && <span className="ptk-chip">{t("payoutTracker.firstPayout")}</span>}
      </span>
      {payout.arrivalAt && <span className="ptk-eta">{t("payoutTracker.arrivesBy", { date: trackerDate(payout.arrivalAt) })}</span>}
      <TrackerBar payout={payout} />
    </span>
    <svg className="ptk-chev" width="9" height="14" viewBox="0 0 9 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m1.5 1.5 5.5 5.5-5.5 5.5" />
    </svg>
  </button>
);

/** Payout History's status cell for a moving payout: "Arrives Nov 2" over a hairline bar. */
export const PayoutRowProgress: React.FC<{ payout: PayoutBatch }> = ({ payout }) => {
  const legs = trackerLegs(payout);
  const done = legs.reduce((a, b) => a + b, 0) / Math.max(1, legs.length);
  return (
    <span className="ptk-row">
      <span className="ptk-row-eta">
        {payout.arrivalAt ? t("payoutTracker.arrives", { date: trackerShortDate(payout.arrivalAt) }) : t("payoutTracker.onTheWay")}
      </span>
      <span className="ptk-row-bar" aria-hidden="true">
        <span style={{ width: `${Math.max(6, Math.round(done * 100))}%` }} />
      </span>
    </span>
  );
};

const bold = (c: string) => <b>{c}</b>;

type SheetProps = {
  payout: PayoutBatch;
  onClose: () => void;
  /** Payout History: the requests behind this payout (PayoutDetailsModal). */
  onSeeRequests?: () => void;
};

/** The full tracker. Portaled: Balance is a clipped one-screen box. */
export function PayoutTrackerSheet({ payout, onClose, onSeeRequests }: SheetProps) {
  const stops = trackerStops(payout);
  const dest = payoutDestinationView(payout);
  const speed = payout.speed;
  return createPortal(
    <BottomSheet open plain title={t("payoutTracker.openTracker")} onClose={onClose} className="ptk-sheet">
      <div className="ptk-head">
        <div>
          <div className="ptk-amount">{payoutAmount(payout)}</div>
          {payout.arrivalAt && <div className="ptk-head-eta">{t("payoutTracker.arrivesBy", { date: trackerDate(payout.arrivalAt) })}</div>}
        </div>
        <button type="button" className="bsheet-x" onClick={onClose} aria-label={t("bottomSheet.close")}>
          <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          </svg>
        </button>
      </div>

      <ol className="ptk-steps">
        {stops.map((s) => (
          <li key={s.label} className={`ptk-step ptk-step--${s.state}`}>
            <span className="ptk-bul" aria-hidden="true">
              {s.state === "done" && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m5 12 5 5 9-10" />
                </svg>
              )}
            </span>
            <span className="ptk-step-text">
              <span className="ptk-step-label">{t(s.label)}</span>
              {s.when && (
                <span className="ptk-step-date">
                  {s.when.kind === "ready" ? (
                    <T k="payoutTracker.readyOn" vars={{ date: s.when.date }} tags={{ 0: bold }} />
                  ) : s.when.kind === "by" ? (
                    t("payoutTracker.byDate", { date: s.when.date })
                  ) : (
                    s.when.date
                  )}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>

      {speed && (
        <div className="ptk-first">
          <div className="ptk-first-head">
            <span className="ptk-first-ic" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
            </span>
            <div>
              <div className="ptk-first-title">{t("payoutTracker.firstTitle")}</div>
              <div className="ptk-first-body">{t("payoutTracker.firstBody")}</div>
            </div>
          </div>
          <div className="ptk-speed">
            <span className="ptk-speed-now">
              <b>{t(aboutDuration(speed.thisTimeDays).key, { count: aboutDuration(speed.thisTimeDays).count })}</b>
              {t("payoutTracker.thisTime")}
            </span>
            <span className="ptk-speed-later">
              <b>{t(aboutDuration(speed.laterDays).key, { count: aboutDuration(speed.laterDays).count })}</b>
              {t("payoutTracker.goingForward")}
            </span>
          </div>
        </div>
      )}

      {(dest || onSeeRequests) && (
        <div className="ptk-foot">
          {dest && (
            <span className="ptk-dest" title={dest.title}>
              <TrackerIcon kind={trackerKind(payout)} size={14} />
              {dest.title}
            </span>
          )}
          {onSeeRequests && (
            <button type="button" className="ptk-see" onClick={onSeeRequests}>
              {t("payoutTracker.seeRequests")}
            </button>
          )}
        </div>
      )}
    </BottomSheet>,
    document.body
  );
}
